import { FunctionComponent, memo, useState } from "react";
import { Box, BoxContent, BoxHeader, BoxHeading } from "@edgetier/client-components";
import { IProps } from "./agent-selection-container.types";
import { Button } from "@edgetier/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDoubleLeft, faChevronDoubleRight, faSave, faSearch, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { IAgent, Route } from "@edgetier/watchtower-types";
import AgentSelectionList from "./agent-selection-list";
import { useQueryClient } from "react-query";
import { axios } from "utilities/axios";
import toast from "react-hot-toast";
import { getServerError } from "utilities/get-server-error";
import { isValueEmpty } from "utilities/is-value-empty";
import "./agent-selection-container.scss";

/**
 * Wrapper to assign agents to a tag. Shows lists with selected and not selected agents for chosen tag
 * @param props.allAgents   All agents availabel
 * @param props.groupAgents Agents in the current tag
 * @param props.tagId       Selected tag ID
 */
const AgentSelectionContainer: FunctionComponent<IProps> = ({ allAgents, groupAgents, tagId }) => {
    const queryClient = useQueryClient();

    const [selectedAgents, setSelectedAgents] = useState<IAgent[]>(groupAgents);
    const [searchValue, setSearchValue] = useState<string>("");

    const notSelectedAgents = allAgents.filter(
        (agent) =>
            selectedAgents.findIndex((selectAgent) => selectAgent.agentId === agent.agentId) < 0 &&
            // If true, it will return all the items that have not been selected. Otherwise, it will do the search.
            (agent.email ? `${agent.name} ${agent.email}` : agent.name)
                .toLowerCase()
                .trim()
                .includes(searchValue.toLowerCase().trim())
    );

    const isSearchActive = !isValueEmpty(searchValue.trim());

    /**
     * Select all agents
     */
    const selectAllAgents = () => {
        setSelectedAgents(selectedAgents.concat(notSelectedAgents));
        setSearchValue("");
    };

    /**
     * Clear all agents selected
     */
    const clearAllAgents = () => {
        setSelectedAgents([]);
        setSearchValue("");
    };

    /**
     * Select a single agent
     * @param selectedAgent agents
     */
    const selectAgent = (selectedAgent: IAgent) => {
        setSelectedAgents((agents) => [...agents, selectedAgent]);
    };

    /**
     * Deselect a single agent
     * @param selectedAgent agents
     */
    const deselectAgent = (selectedAgent: IAgent) => {
        const filterSelectedAgents = selectedAgents.slice().filter((agent) => agent.agentId !== selectedAgent.agentId);
        setSelectedAgents(filterSelectedAgents);
    };

    /**
     * Submit selected agents
     */
    const submitSelectedAgents = async () => {
        const selectedAgentIds = selectedAgents.map((agent) => agent.agentId);
        try {
            await axios.patch(Route.TagsAgents.replace(":tagId", tagId), {
                agentIds: selectedAgentIds,
            });

            queryClient.setQueriesData<IAgent[]>([Route.Agents, { tagId: [tagId] }], () => selectedAgents);

            toast.success("Agents assigned successfully");
        } catch (error) {
            toast.error(getServerError(error));
        }
    };

    return (
        <Box>
            <BoxHeader>
                <BoxHeading>
                    <div className="select-menu__input" data-testid="search">
                        <div className="field field-inline">
                            <div className="field-inline__icon">
                                <FontAwesomeIcon icon={faSearch} />
                            </div>
                            <input
                                autoFocus
                                autoComplete="off"
                                placeholder="Search"
                                type="text"
                                value={searchValue}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setSearchValue(event.target.value.trim())
                                }
                            />
                            {!isValueEmpty(searchValue) && (
                                <button
                                    onClick={() => setSearchValue("")}
                                    type="button"
                                    className="advanced-search-phrase__input__remove-text"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>
                </BoxHeading>
            </BoxHeader>

            <BoxContent>
                <div className="agent-selection-container">
                    <div className="agent-selection-container__lists">
                        <AgentSelectionList
                            label="Not Selected"
                            items={notSelectedAgents}
                            moveAction={selectAgent}
                            isSelectedList={false}
                            isSearchActive={isSearchActive}
                        />

                        <div className="select-bulk-buttons">
                            <Button
                                aria-label="Select all"
                                disabled={notSelectedAgents.length === 0}
                                icon={faChevronDoubleRight}
                                isIconOnly={true}
                                outline={true}
                                title="Select all"
                                onClick={selectAllAgents}
                            />

                            <Button
                                aria-label="Select none"
                                disabled={selectedAgents.length === 0}
                                icon={faChevronDoubleLeft}
                                isIconOnly={true}
                                outline={true}
                                styleName="negative"
                                title="Select none"
                                onClick={clearAllAgents}
                            />
                        </div>
                        <AgentSelectionList
                            label="Selected"
                            items={selectedAgents}
                            moveAction={deselectAgent}
                            isSelectedList={true}
                        />
                    </div>
                    <div className="agent-selection-container__save">
                        <Button icon={faSave} styleName="primary" type="button" onClick={submitSelectedAgents}>
                            Save
                        </Button>
                    </div>
                </div>
            </BoxContent>
        </Box>
    );
};

export default memo(AgentSelectionContainer);
